import * as React from 'react';
import { DatePicker } from '@material-ui/pickers';
import api from '../services/api';
import { AxiosResponse } from 'axios';
import {
  FormControl, InputLabel, MenuItem, Select as RawSelect, makeStyles,
  CircularProgress, ListSubheader, styled,
} from '@material-ui/core';
import { OverviewList } from './components/OverviewList.component';
import { TimesList } from './components/Times.component';
import { utcToZonedTime, format } from 'date-fns-tz';
import {
  startOfMonth, endOfMonth, lightFormat, addDays, addMinutes, isBefore,
  isAfter, parse, isEqual, startOfDay, formatRFC3339,
} from 'date-fns';
import {
  prop, sortBy, reverse, isEmpty, omit, keys
} from 'ramda';

const Select = styled(RawSelect)(() => ({
  '.MuiSelect-root': {
    width: '200px',
    maxWidth: '200px',
  },
  '.MuiSelect-select': {
    paddingRight: '50px',
  },
}));
const useStyles = makeStyles(() => ({
  root: {
    maxWidth: '1000px',
    width: '100%',
    paddingLeft: '24px',
    paddingRight: '24px',
    marginLeft: 'auto',
    marginRight: 'auto'
  },
  overviewContainer: {
    display: 'flex',
    flexDirection: 'row',
    marginTop: '10px',
    marginBottom: '10px'
  },
  overviewListThing: {
    marginRight: '20px',
    display: 'flex',
    flexDirection: 'column',
  },
  officeImageContainer: {
    maxWidth: '322px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 10,
  },
  officeImage: {
    maxWidth: '100%',
    height: 'auto',
    display: 'block',
  },
  dateTimeContainer: {
    marginLeft: '20px',
    display: 'flex',
    flexDirection: 'column',
    width: 550,
  },
  dateTimeContainerPicker: {
    display: 'flex',
    flexDirection: 'row',
    marginTop: '10px',
    marginBottom: '10px'
  },
  timeColumnContainer: {
    display: 'flex',
    flexDirection: 'row',
    marginLeft: '20px',
    maxWidth: '250px'
  },
  showMobile: {
    display: 'none'
  },
  hideMobile: {
    display: 'block'
  },
  '@media screen and (max-width: 700px)': {
    overviewContainer: {
      flexDirection: 'column',
      width: 'unset',
    },
    dateTimeContainer: {
      marginLeft: 0,
      width: 'auto',
    },
    showMobile: {
      display: 'block'
    },
    hideMobile: {
      display: 'none'
    }
  },
  '@media screen and (max-width: 992px)': {
    dateTimeContainerPicker: {
      flexDirection: 'column',
    },
    dateTimeContainer: {
      width: 310,
    },
    timeColumnContainer: {
      marginLeft: '0px',
      marginTop: '20px',
      maxWidth: '350px',
      width: '100%'
    },
  },
  typeOfScheduling: {
    fontSize: 20,
  },
  link: {
    cursor: 'pointer',
    color: '#3795DD',
    whiteSpace: 'nowrap',
  },
}));

import {
  TimeOption, ApiState, Settings, DayTimeOptions, Time,
  AppointmentType, BusyBody, Busy, Hours, SpecialHours,
  SpecialOfficeHours,
} from './np-types';

interface Pro {
  name: string;
  lastName: string;
  appointmentTypes: AppointmentType[];
}

const groupAppointmentTypes = (apptTypes: AppointmentType[]): Pro[] => {
  return apptTypes
    .sort((a, b) => {
      return a.index - b.index;
    })
    .reduce((acc, apptType) => {
      const proName = apptType.displayFirstName + ' ' + apptType.displayLastName;
      const pro = acc.find(({ name }) => name === proName);
      if (pro) {
        pro.appointmentTypes.push(apptType);
        return acc;
      }
      acc.push({
        name: proName,
        appointmentTypes: [apptType]
      });
      return acc;
    }, []);
};

interface FilterBySameDayProps {
  settings: Settings;
  scheduleCutoff: number;
  today?: string; // yyyy-MM-dd
  isTest?: string;
}

export const filterBySameDay = ({
  settings, scheduleCutoff, today, isTest = undefined,
}: FilterBySameDayProps) => (
  times: string[]
): string[] => {
  const now = today ? today : lightFormat(new Date(), 'yyyy-MM-dd');
  const currentOfficeTime = isTest ?
    format(
      addMinutes(
        parse(`${today}T${isTest}`, 'yyyy-MM-dd\'T\'HH:mm:ss', new Date()),
        scheduleCutoff || 0,
      ),
      'yyyy-MM-dd\'T\'HH:mm:ss', {
        timeZone: settings.timezone
      })
    :
    format(
      addMinutes(
        utcToZonedTime(new Date(), settings.timezone),
        scheduleCutoff || 0,
      ),
      'yyyy-MM-dd\'T\'HH:mm:ss', {
        timeZone: settings.timezone
      });
  return times.filter(d => {
    const [localDate] = d.split('T');
    return (settings.allowSameDayScheduling ? true : localDate !== now) &&
        currentOfficeTime < d;
  });
};

interface DisableDateLogicProps {
  settings: Settings;
  today?: string;
  specialHours: SpecialHours[];
  specialOfficeHours: SpecialOfficeHours[];
  hours: Hours[];
}

export const disableDateLogic = ({
  settings, today, specialHours, specialOfficeHours, hours,
}: DisableDateLogicProps) => (d: Date) => {
  const date = lightFormat(d, 'yyyy-MM-dd');
  const now = today ? today : lightFormat(new Date(), 'yyyy-MM-dd');
  if (date < now ||
        (!settings.allowSameDayScheduling ? date === now : false)) {
    return true;
  }
  const day = format(d, 'EEEE');
  const hasSpecial = specialHours.find(({ start, end }) => {
    return (date === end || date < end) &&
        (date === start || date > start);
  });
  if (hasSpecial) {
    return hasSpecial.hours[day.toLowerCase() as string].length === 0;
  }
  const hasSpecialOffice = specialOfficeHours.find((hour) => {
    return date === hour.date;
  });
  if (hasSpecialOffice) {
    return hasSpecialOffice.hours.length === 0;
  }
  const currentHours = hours.find((hour) => {
    return date === hour.date || date > hour.date;
  });
  if (currentHours) {
    return currentHours.hours[day.toLowerCase() as string].length === 0;
  }
  return true;
};

export const convertTime = (timezone: string) => (d: string) => {
  const da = new Date(d);
  const localDate = format(da, 'yyyy-MM-dd', { timeZone: timezone });
  const localTime = format(da, 'yyyy-MM-dd\'T\'HH:mm:ss', { timeZone: timezone });
  const a = format(da, 'a', { timeZone: timezone });
  const display = format(da, 'h:mm a', { timeZone: timezone });
  return {
    a,
    date: localTime,
    localDate,
    localTime,
    display
  };
};

interface TimeSelectProps {
  date: Date;
  time: TimeOption
  apiState: ApiState<Settings>;
  handleDateChange: (d: Date, f: boolean) => void;
  handleTimeSelect: (t: TimeOption) => void;
  apptType: string;
  setApptType: React.Dispatch<React.SetStateAction<string>>;
  selectedApptType: AppointmentType;
  apiKey: string;
  gotoOther: (s: string) => void;
  setModalText: (o: {
    title: string, body: string, actions?: React.ReactNode[]
  }) => void;
}

let currentMonth: Date;

export const TimeSelect = ({
  date, time, apiState, handleDateChange, handleTimeSelect, apptType,
  setApptType, selectedApptType, apiKey, gotoOther, setModalText,
}: TimeSelectProps) => {
  const classes = useStyles();
  const settings = apiState.data;

  const [timesApi, setTimesApi] = React.useState<ApiState<string[]>>({
    isLoading: false,
    errorMessage: '',
    data: []
  });

  /* This state tells the datepicker if it should update the state or
     not. If we don't have this, when you get rediected back to this
     page it'll update the date and you'll be taken back to the
     starting date even if the time is still selected.
  */
  const [allow, setAllow] = React.useState<boolean>(false);
  const [loadingCalendar, setLoadingCalendar] = React.useState<boolean>(false);
  const [hours, setHours] = React.useState<Hours[]>([]);
  const [specialHours, setSpecialHours] = React.useState<SpecialHours[]>([]);
  const [specialOfficeHours, setSpecialOfficeHours] = React.useState<SpecialOfficeHours[]>([]);

  const pros = React.useMemo<Pro[]>(() => {
    return groupAppointmentTypes(apiState.data.newPatientTypes);
  }, [apiState.data.newPatientTypes]);

  const filterBySameDayScheduling = filterBySameDay({
    settings, scheduleCutoff: selectedApptType?.scheduleCutoff,
  });

  const getNewMonth = (firstDay: Date) => {
    if (!apptType) {
      return Promise.resolve();
    }
    const now = time ? new Date(time.date) : firstDay;
    const body: Time = {
      appointmentTypeId: Number(apptType),
      start: lightFormat(startOfMonth(now), 'yyyy-MM-dd'),
      end: lightFormat(endOfMonth(now), 'yyyy-MM-dd'),
    };

    setTimesApi(s => ({ ...s, errorMessage: '', }));

    currentMonth = startOfMonth(now);

    return Promise.all([
      api.post<Time, AxiosResponse<Hours[]>>('hours', body),
      api.post<Time, AxiosResponse<SpecialHours[]>>('specialHours', body),
      api.post<Time, AxiosResponse<SpecialOfficeHours[]>>('officeSpecialHours', body),
    ]).then(([hoursData, specialData, soData]) => {
      setHours(reverse(sortBy(prop('date'), hoursData.data)));
      setSpecialHours(reverse(sortBy(prop('start'), specialData.data)));
      setSpecialOfficeHours(reverse(sortBy(prop('date'), soData.data)));
      setAllow(true);
      setLoadingCalendar(false);
      /*         if (times.length > 0) {
          const hasTime = time && times.find((t) => t === time.date);
          handleDateChange(new Date(times[0]), !hasTime);
        } */
    })
      .catch(e => {
        setLoadingCalendar(false);
        setTimesApi(s => ({ ...s, errorMessage: e.message }));
      });
  };

  const cachedAvailableTimes = React.useMemo(() => {
    return timesApi.data.map(convertTime(settings.timezone));
  }, [timesApi.data]);


  const shouldDisableDate = disableDateLogic({
    settings, specialHours, specialOfficeHours, hours,
  });

  React.useEffect(() => {
    if (Number(apptType) === 0 || !currentMonth) {
      return;
    }
    setTimeout(() => {
      const allDays = document.querySelectorAll('.MuiPickersDay-day');
      allDays.forEach((elem) => {
        if (!elem.className.includes('Disabled')) {
          const day = elem.children[0].children[0].textContent;
          const date = new Date(currentMonth);
          date.setDate(Number(day));
          const busyBody: BusyBody = {
            appointmentType: Number(apptType),
            clients: 1,
            date: lightFormat(date, 'yyyy-MM-dd'),
          };
          api.post<BusyBody, AxiosResponse<Busy[]>>('busyness', busyBody)
            .then((busynessData) => {
              if (isEmpty(busynessData.data)) {
                const reactKeys = keys(elem.parentElement).filter((key) => {
                  return key.includes('react');
                });
                elem.className += ' MuiPickersDay-dayDisabled';
                elem.setAttribute('tabindex', '-1');
                elem.parentElement.onclick = null;
                reactKeys.forEach((key) => {
                  delete elem.parentElement[key];
                });
              }
            });
        }
      });
    }, 100);
  }, [hours, apptType]);

  const dayTimes = React.useMemo<DayTimeOptions>(() => {
    if (!date) {
      return {
        am: [],
        pm: []
      };
    }
    const start = lightFormat(date, 'yyyy-MM-dd');
    const end = lightFormat(addDays(date, 1), 'yyyy-MM-dd');
    const times: TimeOption[] = timesApi.data
      .map(d => {
        const zonedTime = new Date(d);
        const localDate = format(zonedTime, 'yyyy-MM-dd');
        const localTime = format(zonedTime, 'yyyy-MM-dd HH:mm:ss');
        const a = format(zonedTime, 'a',);
        const display = format(zonedTime, 'h:mm a');
        return {
          a,
          display,
          date: d,
          localTime,
          localDate
        };
      }).filter(({ localTime }) => {
        return localTime > start && localTime <= end;
      });
    return {
      am: times.filter(({ a }) => a === 'AM'),
      pm: times.filter(({ a }) => a === 'PM'),
    };
  }, [timesApi.data, date]);

  React.useEffect(() => {
    if (!apptType) {
      return;
    }
    const now = time ? new Date(time.date) : new Date();
    const body: Time = {
      appointmentTypeId: Number(apptType),
      start: lightFormat(startOfMonth(now), 'yyyy-MM-dd'),
      end: lightFormat(endOfMonth(now), 'yyyy-MM-dd'),
    };
    setLoadingCalendar(true);

    Promise.all([
      api.post<Time, AxiosResponse<Hours[]>>('hours', body),
      api.post<Time, AxiosResponse<SpecialHours[]>>('specialHours', body),
      api.post<Time, AxiosResponse<SpecialOfficeHours[]>>('officeSpecialHours', body),
    ]).then(([hoursData, specialData, soData]) => {
      setHours(reverse(sortBy(prop('date'), hoursData.data)));
      setSpecialHours(reverse(sortBy(prop('start'), specialData.data)));
      setSpecialOfficeHours(reverse(sortBy(prop('date'), soData.data)));
      setAllow(true);
      setLoadingCalendar(false);
    })
      .catch(e => setLoadingCalendar(false));
  }, [apptType]);

  React.useEffect(() => {
    if (!apptType) {
      return;
    }
    const now = time ? new Date(time.date) : date;
    const busyBody: BusyBody = {
      appointmentType: Number(apptType),
      clients: 1,
      date: lightFormat(now, 'yyyy-MM-dd'),
    };
    setTimesApi(s => ({ ...s, isLoading: true, errorMessage: '' }));
    api.post<BusyBody, AxiosResponse<Busy[]>>('busyness', busyBody)
      .then((busynessData) => {
        const times = filterBySameDayScheduling(busynessData.data.map(({ time }) => time));
        currentMonth = date;
        setTimesApi(s => ({ ...s, isLoading: false, data: times }));
      }).catch(e => setTimesApi(s => ({ ...s, isLoading: false, errorMessage: e.message })));
  }, [apptType, date]);

  const [prompt, linkedUrl, linkPrompt] = React.useMemo(() => {
    if (!settings.linkedKey) {
      return ['', '', ''];
    }
    if (settings?.nppType === 'New') {
      return [
        'Have you been with us before?',
        `/new-patient/contact?key=${settings.linkedKey}&from=${apiKey}`,
        'Returning Patients Here'
      ];
    } else {
      return [
        'Is this your first time with us?',
        `/new-patient/time?key=${settings.linkedKey}&from=${apiKey}`,
        'New Patients Here'
      ];
    }
  }, [settings?.nppType, settings?.linkedKey]);

  const setApptTypeEvent = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (val) {
      setApptType(val);
    }
  };

  return (
    <div className={classes.overviewContainer}>
      <div
        className={classes.overviewListThing}>
        {settings.logoUrl &&
        <div className={classes.officeImageContainer}>
          <img
            src={settings.logoUrl}
            alt={settings.name}
            className={classes.officeImage}
          />
        </div>
        }
        <div className={classes.hideMobile}>
          <OverviewList
            date={date}
            time={time}
            timezone={settings?.timezone}
          />
        </div>
        <div
          style={{ maxWidth: '400px' }}
          dangerouslySetInnerHTML={{ __html: settings.landingPageDescription }}>
        </div>
      </div>


      <div className={classes.dateTimeContainer}>
        <div className={classes.typeOfScheduling}>
          <b>{settings.nppType} Patient</b> <i>Scheduling</i>
        </div>
        <h4>{settings.calendarDirections}</h4>
        {settings.newPatientTypes.length > 1 &&
          <FormControl style={{ width: '250px' }}>
            <InputLabel id="appt-code-label">Appointment Type</InputLabel>
            {apiState.data.hidePro ?
              <Select
                value={apptType}
                onChange={setApptTypeEvent}
                labelId="appt-code-label"
                id="appt-code-select">
                {pros.map(pro => (
                  pro.appointmentTypes.map(t => (
                    <MenuItem
                      key={t.appointmentTypeId}
                      value={t.appointmentTypeId}>
                      {t.displayName}
                    </MenuItem>))
                ))}
              </Select> :
              <Select
                value={Number(apptType)}
                onChange={setApptTypeEvent}
                labelId="appt-code-label"
                id="appt-code-select">
                {pros.map(pro => ([
                  <ListSubheader>{pro.name}</ListSubheader>,
                  pro.appointmentTypes.map(t => (
                    <MenuItem
                      key={t.appointmentTypeId}
                      value={t.appointmentTypeId}>
                      {t.displayName}
                    </MenuItem>))
                ]))}
              </Select>}
          </FormControl>}
        <div className={classes.dateTimeContainerPicker}>
          {loadingCalendar || apiState.isLoading && <CircularProgress />}
          {!loadingCalendar && <div style={{ margin: '0 auto' }}>
            <DatePicker
              autoOk
              disableToolbar
              orientation="landscape"
              variant="static"
              openTo="date"
              style={{ maxWidth: '310px' }}
              shouldDisableDate={apiState.isLoading ? () => true : shouldDisableDate}
              onMonthChange={getNewMonth}
              value={date}
              onChange={(d: Date) => {
                if (allow)
                  handleDateChange(d, false);
              }}
            />
            {prompt &&
              <p style={{ color: '#444F53' }}>
                <span style={{ marginRight: 3 }}>
                  {prompt}
                </span>
                <span
                  className={classes.link}
                  onClick={() => {
                    if (settings.linkedKey)
                      gotoOther(linkedUrl);
                    else
                      setModalText({
                        title: 'Contact Our Office',
                        body: `We apologize for the inconvenience. This scheduler isn\'t built for ${linkPrompt.split(' ')[0].toLowerCase()} patients. We kindly ask you to contact our office.`,
                      });
                  }}
                >
                  {linkPrompt}
                </span>
              </p>}
          </div>}
          {timesApi.data.length === 0 &&
            <div className={classes.timeColumnContainer}>
              <p style={{ width: 300 }}>
                There are no more available times left on this day! Select another day to view available times.
              </p>
            </div>}
          <div className={classes.timeColumnContainer} style={{
            width: 220
          }}>
            {timesApi.isLoading && <CircularProgress />}
            {timesApi.data.length > 0 && !timesApi.isLoading &&
                <>
                  {dayTimes.am.length > 0 &&
                     <TimesList
                       title="AM"
                       times={dayTimes.am}
                       time={time}
                       handleTimeSelect={handleTimeSelect}
                       disabled={apiState.isLoading}
                     />}
                  <div style={{ flexGrow: 1, paddingLeft: '20px' }}></div>
                  {dayTimes.pm.length > 0 &&
                    <TimesList
                      title="PM"
                      times={dayTimes.pm}
                      time={time}
                      handleTimeSelect={handleTimeSelect}
                      disabled={apiState.isLoading}
                    />}
                </>}
          </div>
        </div>
        <div className={classes.showMobile} style={{ marginTop: '20px' }}>
          <OverviewList
            date={date}
            time={time}
            timezone={apiState.data?.timezone}
          />
        </div>
      </div>
    </div>
  );
};
